import React from "react";

import type { HistoryBackedStateOptions } from "@/main/react/page/useHistoryBackedState";

/**
 * One history entry per typed-text EDITING SESSION, rather than one per keystroke (§13.4 of
 * `designs/01-now/2026-07-25-history-entry-ownership-and-in-page-back-navigation-design.md`).
 *
 * §13.1 says a member action that changes the address bar must push. A debounced search box taken
 * literally satisfies that and violates its point: Back through `poetry` would walk `poetr`, `poet`,
 * `poe`, `po`, `p` before it reached anywhere the member would call a previous address. So the FIRST
 * write of a session pushes and the rest carry `continue-editing`; Back therefore lands on the address
 * from before the search began, which is the previous address a member would name.
 *
 * <p><b>Why this is a shared helper and not a `useRef` in each page.</b> There are two consumers today
 * and the ref they need is identical. Two copies of one rule drift silently — the same argument rule 31
 * makes about two copies of one value, one layer out — and the drift here is invisible: a page whose
 * session tracking is subtly wrong renders correctly in every frame and only shows up as a Back press
 * that lands somewhere odd. `historyBackInvariant.spec.tsx` asserts the shape once, here.
 *
 * <p><b>The session ends on three things</b>, all of which mean the member has stopped typing THIS
 * search: the box going empty, an explicit clear, and moving to another control (a tag chip, a scope
 * toggle). Each of those is itself a member action, so each pushes on its own account; ending the
 * session is what makes the NEXT keystroke push rather than extend an entry nobody is editing.
 */
export type TypedTextHistorySession = {
    /**
     * The setter options for writing `nextValue` into the URL. `undefined` means push.
     *
     * Emptying the box is treated as the last write of the session rather than the first of a new one:
     * backspacing `poetry` away is one continuous edit, and pushing on the empty state would hand the
     * member an entry they reached by deleting.
     */
    optionsForTypedValue: (nextValue: string) => HistoryBackedStateOptions | undefined;
    /** End the session without writing — the member moved to another control. */
    endTypingSession: () => void;
};

export function useTypedTextHistorySession(): TypedTextHistorySession {
    // True once this session has pushed its opening entry.
    const sessionOpenRef = React.useRef(false);

    const optionsForTypedValue = React.useCallback((nextValue: string): HistoryBackedStateOptions | undefined => {
        if (!nextValue.trim()) {
            const wasOpen = sessionOpenRef.current;
            sessionOpenRef.current = false;
            return wasOpen ? { replace: "continue-editing" } : undefined;
        }

        if (!sessionOpenRef.current) {
            sessionOpenRef.current = true;
            return undefined;
        }

        return { replace: "continue-editing" };
    }, []);

    const endTypingSession = React.useCallback(() => {
        sessionOpenRef.current = false;
    }, []);

    return { optionsForTypedValue, endTypingSession };
}
